import { findingSortMethod_Alphabet, findingSortMethod_BySystem, findingSortMethod_Static, findingSortMethod_Timestamp } from '@app/advisor-ui-shared/index';
import { FindingType_Range } from '@app/advisor-shared';
import { ModuleFE_Advisor_ContextRule, ModuleFE_Advisor_Variable } from '../../healthcare-space.js';
import { _entries, _values, filterInstances, MissingDataException, sortArray, StaticLogger } from '@nu-art/ts-common';
import { AdvisorConfigKeyFE_SystemTagIds } from '../../app-config.js';
import { ModuleFE_Advisor_Action } from '../../pathway.js';
import { ModuleFE_RuntimeFinding } from '../../../../_entity/runtime/runtime-finding/index.js';
import { ModuleFE_RuntimeOrder } from '../../../../_entity/runtime/runtime-order/index.js';
import { ModuleFE_AdvisorSession } from '../../../../_entity/index.js';
export const noGroupIndicator = '###NOGROUP###';
export function getSectionConfigFilteredFindings(config, onlyAbnormalResults) {
    const findings = ModuleFE_RuntimeFinding.getAllFindingsToRender(onlyAbnormalResults, config.forceShowNegative).filter(finding => {
        //Fail fast if the finding itself is flagged hidden by the backend (e.g. CareMap disease suspicions)
        if (finding.showInAdvisor === false)
            return false;
        //Fail fast if finding is chief complaint but the section does not show chief complaints
        if (finding.isChiefComplaint && !config.showChiefComplaints)
            return false;
        //Fail fast if finding is not chief complaint and the section shows only chief complaints
        if (config.showOnlyChiefComplaints && !finding.isChiefComplaint)
            return false;
        //Fail fast finding variable is hidden in advisor
        const variable = ModuleFE_Advisor_Variable.cache.unique(finding.variableId);
        return !(!variable || !variable.showInAdvisor);
    });
    //Return the findings sorted by timestamp, latest first
    return sortArray(findings, finding => finding.issuedTimestamp ?? finding.__updated, true);
}
export function filterFindingsByContextRules(findings, contextRuleId) {
    return findings.filter(finding => {
        if (finding.type === FindingType_Range)
            return false;
        const variable = ModuleFE_Advisor_Variable.cache.unique(finding.variableId);
        if (!variable) {
            StaticLogger.logDebugBold(`Could not find variable for id ${finding.variableId}`, finding);
            return false;
        }
        return ModuleFE_Advisor_ContextRule.variablePassesRule(contextRuleId, variable);
    });
}
export function groupFindingsIntoResults(findings) {
    const map = findings.reduce((map, finding) => {
        if (!map[finding.variableId]) {
            const variable = ModuleFE_Advisor_Variable.cache.unique(finding.variableId);
            if (!variable)
                throw new MissingDataException(`Could not find variable for id ${finding.variableId}`);
            map[finding.variableId] = { variable: variable, findings: [] };
        }
        map[finding.variableId].findings.push(finding);
        return map;
    }, {});
    return _values(map);
}
export function sortFindingResults(findingResults, sortMethod) {
    switch (sortMethod) {
        case findingSortMethod_Static:
            return findingResults;
        case findingSortMethod_Alphabet:
            return sortArray(findingResults, result => result.variable._nameInLowercase);
        case findingSortMethod_Timestamp:
            return sortArray(findingResults, result => result.findings[0].issuedTimestamp ?? result.findings[0].__updated, true);
        case findingSortMethod_BySystem: {
            const systemTagIds = AdvisorConfigKeyFE_SystemTagIds.get() ?? [];
            //Sort By Name
            const nameSorted = sortFindingResults(findingResults, findingSortMethod_Alphabet);
            return sortArray(nameSorted, result => {
                const _systemTagIds = result.variable._tagIds.filter(id => systemTagIds.includes(id));
                return Math.min(..._systemTagIds.map(id => systemTagIds.indexOf(id)));
            });
        }
    }
}
export function sortSpecialAttentionResultsToStart(findingResults) {
    return sortArray(findingResults, result => {
        const specialAttentions = ModuleFE_RuntimeFinding.getSpecialAttention(result.variable);
        return _values(specialAttentions).some(value => value) ? 0 : 1;
    });
}
export function groupFindingsIntoResultsByOrder(findings, findingSortMethod, orderContextRuleId) {
    //Group all findings under rtOrderId / noGroupIndicator
    const currentEncounterId = ModuleFE_AdvisorSession.getCurrentEncounterId();
    const groupedByRTOrderId = findings.reduce((map, finding) => {
        const indicator = finding.runtimeOrderId ?? noGroupIndicator;
        //Not showing ungrouped findings that are of the current encounter
        if (indicator === noGroupIndicator && finding.encounterId === currentEncounterId)
            return map;
        if (!map[indicator])
            map[indicator] = [];
        map[indicator].push(finding);
        return map;
    }, {});
    //Translate the grouped findings into blocks
    const blocks = filterInstances(_entries(groupedByRTOrderId).map(({ key, value }) => {
        let findingResults = groupFindingsIntoResults(value);
        findingResults = sortFindingResults(findingResults, findingSortMethod);
        const rtOrder = ModuleFE_RuntimeOrder.cache.unique(key);
        if (!rtOrder && key !== noGroupIndicator)
            return;
        return { rtOrder, results: findingResults };
    }));
    //Sort blocks by rt order timestamp, latest first
    sortArray(blocks, block => block.rtOrder?.timeResulted ?? 0, true);
    //Group blocks under db-orders
    const groupedByOrderId = blocks.reduce((map, block) => {
        const indicator = block.rtOrder?.orderId ?? noGroupIndicator;
        if (!map[indicator])
            map[indicator] = [];
        map[indicator].push(block);
        return map;
    }, {});
    //Translate grouped blocks into the final results by order
    const resultByOrder = _entries(groupedByOrderId).map(({ key, value }) => {
        const order = ModuleFE_Advisor_Action.cache.unique(key);
        if (!order && key !== noGroupIndicator)
            throw new MissingDataException(`Could not find order for id ${key}`);
        return { order, blocks: value };
    });
    //Sort results by timestamp of latest runtime order, latest first
    sortArray(resultByOrder, result => result.blocks[0].rtOrder?.timeResulted ?? 0, true);
    //Filter out orders by context rule
    if (!orderContextRuleId)
        return resultByOrder;
    return resultByOrder.filter(result => {
        if (!result.order)
            return true;
        const orderVar = ModuleFE_Advisor_Variable.cache.unique(result.order.variableId);
        if (!orderVar)
            throw new MissingDataException(`Could not find variable for id ${result.order.variableId}`);
        return ModuleFE_Advisor_ContextRule.variablePassesRule(orderContextRuleId, orderVar);
    });
}
//# sourceMappingURL=utils.js.map